import React, { useState } from "react";
import { FaEye, FaTimes, FaBox, FaArrowUp, FaArrowDown, FaCalendarAlt } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";

const DetailAtk = ({ id, item, masuks, keluars }) => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const openDetailModal = () => setIsDetailModalOpen(true);
  const closeDetailModal = () => setIsDetailModalOpen(false);

  // Fungsi untuk mendapatkan data masuk berdasarkan ATK ID
  const getMasuk = (atkId) => {
    return masuks.filter((item) => item.atk_id === atkId);
  };

  // Fungsi untuk mendapatkan data keluar berdasarkan ATK ID
  const getKeluar = (atkId) => {
    return keluars.filter((item) => item.atk_id === atkId);
  };

  // Fungsi untuk menghitung total masuk
  const getTotalMasuk = (atkId) => {
    return getMasuk(atkId).reduce((total, item) => total + (item.jml || 0), 0);
  };

  // Fungsi untuk menghitung total keluar
  const getTotalKeluar = (atkId) => {
    return getKeluar(atkId).reduce((total, item) => total + (item.vol || 0), 0);
  };

  // Fungsi untuk menghitung stok akhir
  const calculateStokAkhir = (stokAwal, totalMasuk, totalKeluar) => {
    return stokAwal + totalMasuk - totalKeluar;
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20 
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        duration: 0.4, 
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: { 
        duration: 0.3, 
        ease: "easeIn" 
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { delay: 0.1, duration: 0.3 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { delay: 0.2, duration: 0.3 }
    }
  };

  const closeButtonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { delay: 0.1, duration: 0.3 }
    },
    hover: { 
      scale: 1.1,
      rotate: 90,
      transition: { duration: 0.2 }
    }
  };

  const dataMasuk = getMasuk(id);
  const dataKeluar = getKeluar(id);
  const totalMasuk = getTotalMasuk(id);
  const totalKeluar = getTotalKeluar(id);
  const stokAkhir = calculateStokAkhir(item?.stok_awal || 0, totalMasuk, totalKeluar);

  return (
    <>
      <button 
        onClick={openDetailModal}
        className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-all duration-200"
      >
        <FaEye className="text-sm" />
      </button>

      {/* ==================== DETAIL MODAL ==================== */}
      <AnimatePresence>
        {isDetailModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeDetailModal}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <motion.div 
                  className="flex items-center justify-between"
                  variants={headerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FaBox className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        Detail Alat Tulis Kantor
                      </h3>
                      <p className="text-sm text-gray-500">
                        {item?.nabar || 'Nama tidak tersedia'}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    onClick={closeDetailModal}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                    variants={closeButtonVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <FaTimes />
                  </motion.button>
                </motion.div>
              </div>

              {/* Content */}
              <motion.div 
                className="p-6 space-y-6"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Informasi Dasar ATK */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Informasi Barang</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kode:</span>
                        <span className="font-medium">{item?.kode || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Nama:</span>
                        <span className="font-medium">{item?.nabar || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Spesifikasi:</span>
                        <span className="font-medium">{item?.spec || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Satuan:</span>
                        <span className="font-medium">{item?.satuan || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kategori:</span>
                        <span className="font-medium capitalize">{item?.kategori?.replace('-', ' ') || '-'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Informasi Stok</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Stok Awal:</span>
                        <span className="font-medium">{(item?.stok_awal || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">Total Masuk:</span>
                        <span className="font-medium text-green-600">+{totalMasuk.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">Total Keluar:</span>
                        <span className="font-medium text-red-600">-{totalKeluar.toLocaleString()}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between">
                        <span className="text-gray-900 font-semibold">Stok Akhir:</span>
                        <span className={`font-bold ${stokAkhir <= 10 ? 'text-red-600' : stokAkhir <= 20 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {stokAkhir.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Riwayat Transaksi */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Data Masuk */}
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <FaArrowUp className="text-green-600" />
                      <h4 className="font-semibold text-green-800">Riwayat Barang Masuk</h4>
                      <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs">
                        {dataMasuk.length} transaksi
                      </span>
                    </div>
                    
                    {dataMasuk.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {dataMasuk
                          .sort((a, b) => new Date(b.tgl) - new Date(a.tgl))
                          .map((masuk, index) => (
                            <div key={index} className="bg-white rounded-lg p-3 border border-green-200">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <FaCalendarAlt className="text-green-600 text-xs" />
                                    <span className="text-sm text-gray-600">
                                      {dayjs(masuk.tgl).format('DD/MM/YYYY')}
                                    </span>
                                  </div>
                                  {masuk.keterangan && (
                                    <p className="text-xs text-gray-500 mt-1">{masuk.keterangan}</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className="text-lg font-bold text-green-600">
                                    +{(masuk.jml || 0).toLocaleString()}
                                  </span>
                                  <p className="text-xs text-gray-500">{item?.satuan}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FaArrowUp className="mx-auto text-3xl mb-2 opacity-50" />
                        <p>Belum ada data barang masuk</p>
                      </div>
                    )}
                  </div>

                  {/* Data Keluar */}
                  <div className="bg-red-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <FaArrowDown className="text-red-600" />
                      <h4 className="font-semibold text-red-800">Riwayat Barang Keluar</h4>
                      <span className="bg-red-200 text-red-800 px-2 py-1 rounded-full text-xs">
                        {dataKeluar.length} transaksi
                      </span>
                    </div>
                    
                    {dataKeluar.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {dataKeluar
                          .sort((a, b) => new Date(b.tgl) - new Date(a.tgl))
                          .map((keluar, index) => (
                            <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <FaCalendarAlt className="text-red-600 text-xs" />
                                    <span className="text-sm text-gray-600">
                                      {dayjs(keluar.tgl).format('DD/MM/YYYY')}
                                    </span>
                                  </div>
                                  <div className="lowercase text-xs text-slate-400">{keluar.pengambil}</div>
                                  {keluar.keterangan && (
                                    <p className="text-xs text-gray-500 mt-1">{keluar.keterangan}</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className="text-lg font-bold text-red-600">
                                    -{(keluar.vol || 0).toLocaleString()}
                                  </span>
                                  <p className="text-xs text-gray-500">{item?.satuan}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FaArrowDown className="mx-auto text-3xl mb-2 opacity-50" />
                        <p>Belum ada data barang keluar</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-primary/5 rounded-xl p-4 border border-secondary">
                  <h4 className="font-semibold text-blue-900 mb-3">Ringkasan</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{(item?.stok_awal || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-600">Stok Awal</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">+{totalMasuk.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">Total Masuk</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">-{totalKeluar.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">Total Keluar</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${stokAkhir <= 10 ? 'text-red-600' : stokAkhir <= 20 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {stokAkhir.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">Stok Akhir</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DetailAtk;